import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, X, FileText, Upload, Server, Search, CheckCircle2,
  AlertTriangle, FolderSearch, CheckSquare, Square
} from 'lucide-react';
import { apiService } from '@/services/api';
import { ResultadoImportacao } from './cotacao-types';

/**
 * O backend lê cada PDF em 2–5 s. Mandar as 248 ARs numa requisição só estouraria
 * qualquer proxy, então a tela fatia em lotes e vai mostrando o progresso — o
 * que também deixa o resultado parcial utilizável se algo falhar no meio.
 */
const TAMANHO_LOTE = 10;

interface Props {
  cotacaoId: string;
  onFechar: () => void;
  /** Chamado ao final, quando algo foi de fato importado. */
  onImportou: () => void;
}

type Aba = 'servidor' | 'upload';

interface ArquivoServidor {
  nome: string;
  importado: boolean;
}

export function ImportarArsModal({ cotacaoId, onFechar, onImportou }: Props) {
  const [aba, setAba] = useState<Aba>('servidor');

  const [arquivosServidor, setArquivosServidor] = useState<ArquivoServidor[]>([]);
  const [diretorio, setDiretorio] = useState<string>('');
  const [pastaDisponivel, setPastaDisponivel] = useState(true);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [busca, setBusca] = useState('');

  const [arquivosLocais, setArquivosLocais] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [importando, setImportando] = useState(false);
  const [progresso, setProgresso] = useState({ feitos: 0, total: 0 });
  const [resultados, setResultados] = useState<ResultadoImportacao[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const carregarLista = useCallback(async () => {
    try {
      const res = await apiService.listarArsDoServidor(cotacaoId);
      if (res.success && res.data) {
        setArquivosServidor(res.data.arquivos ?? []);
        setDiretorio(res.data.diretorio ?? '');
        setPastaDisponivel(Boolean(res.data.disponivel));
        if (!res.data.disponivel) setAba('upload');
      }
    } catch {
      setPastaDisponivel(false);
      setAba('upload');
    } finally {
      setCarregandoLista(false);
    }
  }, [cotacaoId]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return arquivosServidor;
    return arquivosServidor.filter((a) => a.nome.toLowerCase().includes(termo));
  }, [arquivosServidor, busca]);

  const disponiveis = useMemo(() => filtrados.filter((a) => !a.importado), [filtrados]);
  const todosSelecionados = disponiveis.length > 0 && disponiveis.every((a) => selecionados.has(a.nome));

  const alternar = (nome: string) => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(nome)) proximo.delete(nome);
      else proximo.add(nome);
      return proximo;
    });
  };

  const alternarTodos = () => {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (todosSelecionados) disponiveis.forEach((a) => proximo.delete(a.nome));
      else disponiveis.forEach((a) => proximo.add(a.nome));
      return proximo;
    });
  };

  /** Percorre os lotes acumulando resultados; nunca aborta no primeiro erro. */
  const executarEmLotes = async <T,>(
    itens: T[],
    enviarLote: (lote: T[]) => Promise<ResultadoImportacao[]>
  ) => {
    setImportando(true);
    setErro(null);
    setResultados([]);
    setProgresso({ feitos: 0, total: itens.length });

    const acumulado: ResultadoImportacao[] = [];
    for (let i = 0; i < itens.length; i += TAMANHO_LOTE) {
      const lote = itens.slice(i, i + TAMANHO_LOTE);
      try {
        acumulado.push(...(await enviarLote(lote)));
      } catch (e) {
        acumulado.push(
          ...lote.map((item) => ({
            arquivo: String((item as any)?.name ?? item),
            sucesso: false,
            erro: e instanceof Error ? e.message : 'Falha de rede',
          }))
        );
      }
      setResultados([...acumulado]);
      setProgresso({ feitos: Math.min(i + TAMANHO_LOTE, itens.length), total: itens.length });
    }

    setImportando(false);
    if (acumulado.some((r) => r.sucesso)) {
      onImportou();
      await carregarLista();
      setSelecionados(new Set());
      setArquivosLocais([]);
    }
  };

  const importarDoServidor = () =>
    executarEmLotes(Array.from(selecionados), async (lote) => {
      const res = await apiService.importarArsDoServidor(cotacaoId, lote);
      if (!res.success) throw new Error(res.message || 'Erro ao importar');
      return (res.data?.resultados ?? []) as ResultadoImportacao[];
    });

  const importarLocais = () =>
    executarEmLotes(arquivosLocais, async (lote) => {
      const res = await apiService.importarArsEnviadas(cotacaoId, lote);
      if (!res.success) throw new Error(res.message || 'Erro ao importar');
      return (res.data?.resultados ?? []) as ResultadoImportacao[];
    });

  const sucessos = resultados.filter((r) => r.sucesso);
  const falhas = resultados.filter((r) => !r.sucesso);
  const podeImportar = aba === 'servidor' ? selecionados.size > 0 : arquivosLocais.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-white dark:bg-schmersal-gray-darker rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div>
            <h2 className="font-bold text-schmersal-blue dark:text-white">Importar Apreciações de Risco</h2>
            <p className="text-xs text-schmersal-gray-dark dark:text-schmersal-gray-light mt-0.5">
              Cada PDF vira uma máquina com suas fotos e seus pontos de risco já separados entre adequados e não adequados.
            </p>
          </div>
          <button onClick={onFechar} disabled={importando} className="p-1 text-schmersal-gray-medium hover:text-schmersal-blue disabled:opacity-40">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-1 px-5 pt-4">
          <BotaoAba ativo={aba === 'servidor'} onClick={() => setAba('servidor')} icone={Server} disabled={!pastaDisponivel}>
            Pasta do servidor
          </BotaoAba>
          <BotaoAba ativo={aba === 'upload'} onClick={() => setAba('upload')} icone={Upload}>
            Enviar do computador
          </BotaoAba>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {aba === 'servidor' ? (
            carregandoLista ? (
              <div className="flex items-center justify-center py-12 text-schmersal-gray-medium">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lendo a pasta...
              </div>
            ) : !pastaDisponivel ? (
              <div className="text-center py-12">
                <FolderSearch className="h-9 w-9 mx-auto text-schmersal-gray-medium mb-2" />
                <p className="text-sm text-schmersal-gray-dark dark:text-schmersal-gray-light">
                  A pasta de ARs do servidor não está acessível. Use o envio pelo computador.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[11px] font-mono text-schmersal-gray-medium mb-2 truncate" title={diretorio}>
                  {diretorio}
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
                    <input
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Filtrar por nome do equipamento"
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-schmersal-gray-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-schmersal-blue"
                    />
                  </div>
                  <button
                    onClick={alternarTodos}
                    disabled={disponiveis.length === 0}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-schmersal-gray dark:border-schmersal-gray-dark text-schmersal-gray-dark dark:text-schmersal-gray-light hover:border-schmersal-blue disabled:opacity-40 whitespace-nowrap"
                  >
                    {todosSelecionados ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    {todosSelecionados ? 'Limpar' : `Todas (${disponiveis.length})`}
                  </button>
                </div>

                <div className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark max-h-[340px] overflow-y-auto">
                  {filtrados.length === 0 ? (
                    <p className="p-4 text-sm text-schmersal-gray-medium text-center">Nenhum arquivo para este filtro.</p>
                  ) : (
                    filtrados.map((arquivo) => (
                      <label
                        key={arquivo.nome}
                        className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${
                          arquivo.importado
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-schmersal-gray-light/50 dark:hover:bg-schmersal-gray-dark/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={arquivo.importado || importando}
                          checked={selecionados.has(arquivo.nome)}
                          onChange={() => alternar(arquivo.nome)}
                          className="h-4 w-4 accent-schmersal-blue flex-shrink-0"
                        />
                        <FileText className="h-4 w-4 text-schmersal-gray-medium flex-shrink-0" />
                        <span className="flex-1 truncate text-schmersal-gray-dark dark:text-schmersal-gray-light" title={arquivo.nome}>
                          {arquivo.nome.replace(/\.pdf$/i, '')}
                        </span>
                        {arquivo.importado && (
                          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                            já importada
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </>
            )
          ) : (
            <div>
              <button
                onClick={() => inputRef.current?.click()}
                disabled={importando}
                className="w-full border-2 border-dashed border-schmersal-gray dark:border-schmersal-gray-dark rounded-lg py-10 text-center hover:border-schmersal-blue transition-colors disabled:opacity-50"
              >
                <Upload className="h-8 w-8 mx-auto text-schmersal-gray-medium mb-2" />
                <p className="text-sm font-semibold text-schmersal-blue dark:text-white">Selecionar PDFs de AR</p>
                <p className="text-xs text-schmersal-gray-medium mt-1">Pode escolher vários de uma vez</p>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                className="hidden"
                onChange={(e) => setArquivosLocais(Array.from(e.target.files ?? []))}
              />

              {arquivosLocais.length > 0 && (
                <div className="mt-3 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark max-h-[240px] overflow-y-auto">
                  {arquivosLocais.map((arquivo, i) => (
                    <div key={`${arquivo.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <FileText className="h-4 w-4 text-schmersal-gray-medium flex-shrink-0" />
                      <span className="flex-1 truncate text-schmersal-gray-dark dark:text-schmersal-gray-light">{arquivo.name}</span>
                      <span className="text-xs text-schmersal-gray-medium">{(arquivo.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {erro && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{erro}</span>
            </div>
          )}

          {resultados.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-3 text-sm font-semibold mb-2">
                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" /> {sucessos.length} importada(s)
                </span>
                {falhas.length > 0 && (
                  <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" /> {falhas.length} com problema
                  </span>
                )}
              </div>
              <div className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark max-h-[200px] overflow-y-auto text-xs">
                {resultados.map((r, i) => (
                  <div key={`${r.arquivo}-${i}`} className="px-3 py-2 flex items-start gap-2">
                    {r.sucesso ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-schmersal-gray-dark dark:text-schmersal-gray-light font-medium">
                        {r.maquina ?? r.arquivo}
                      </p>
                      <p className="text-schmersal-gray-medium">
                        {r.sucesso
                          ? `${r.partes} parte(s) · ${r.naoAdequadas} a cotar${r.avisos?.length ? ` · ${r.avisos.join(' · ')}` : ''}`
                          : r.erro}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <div className="text-xs text-schmersal-gray-medium">
            {importando
              ? `Lendo ${progresso.feitos} de ${progresso.total}... cada AR leva alguns segundos.`
              : podeImportar
              ? `${aba === 'servidor' ? selecionados.size : arquivosLocais.length} arquivo(s) selecionado(s)`
              : 'Selecione ao menos um arquivo'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onFechar}
              disabled={importando}
              className="px-4 py-2 text-sm font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:underline disabled:opacity-40"
            >
              {resultados.length > 0 ? 'Concluir' : 'Cancelar'}
            </button>
            <button
              onClick={aba === 'servidor' ? importarDoServidor : importarLocais}
              disabled={!podeImportar || importando}
              className="flex items-center gap-2 px-4 py-2 bg-schmersal-blue hover:bg-schmersal-blue-light disabled:opacity-50 text-white rounded-lg font-semibold text-sm transition-colors"
            >
              {importando && <Loader2 className="h-4 w-4 animate-spin" />}
              Importar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BotaoAba({
  ativo, onClick, icone: Icone, children, disabled,
}: {
  ativo: boolean;
  onClick: () => void;
  icone: typeof Server;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-semibold border-b-2 transition-colors disabled:opacity-40 ${
        ativo
          ? 'border-schmersal-turquoise text-schmersal-blue dark:text-white'
          : 'border-transparent text-schmersal-gray-medium hover:text-schmersal-blue'
      }`}
    >
      <Icone className="h-4 w-4" />
      {children}
    </button>
  );
}
